import { useApi } from '@/api';
import { Page, RecentActivity } from '@/components';
import {
  Flex,
  InfinityIcon,
  KpiCard,
  KpiSkeleton,
  WalletAddress
} from '@blockanalitica/ui';
import { useCallback } from 'react';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';
import BorrowPositionsCardList from './BorrowPositionsCardList';
import HistoricTabs from './HistoricTabs';
import SupplyPositionsCardList from './SupplyPositionsCardList';
import { DEFAULT_NETWORK } from '@/constants';

export type Wallet = {
  wallet_address: string;
  total_supply: string;
  total_supply_change: string | number;
  total_supply_change_percentage: string | number;
  total_borrow: string;
  total_borrow_change: string | number;
  total_borrow_change_percentage: string | number;
  net: string;
  liquidation_buffer: string;
  liquidation_buffer_change: string | number;
  liquidation_buffer_change_percentage: string | number;
  account_liquidity: string;
  account_liquidity_change: string | number;
  account_liquidity_change_percentage: string | number;
  health_rate: string;
  health_rate_change: string | number;
  health_rate_change_percentage: string | number;
  emode: number;
};

export type ActivityEvent = {
  datetime: string;
  date: string;
  tx_hash: string;
  block_number: string;
  order_index: string;
  wallet_address: string;
  event: string;
  underlying_address: string;
  symbol: string;
  debt_symbol?: string;
  amount: string;
  amount_usd: string;
};

export default function WalletPage() {
  const theme = useTheme();
  const { walletAddress } = useParams() as {
    walletAddress: string;
  };
  const network = DEFAULT_NETWORK;

  const [data, error] = useApi<Wallet>({
    url: `/wallets/${walletAddress}/`
  });

  const contentFn = useCallback(
    (item: ActivityEvent) => (
      <>
        {item.event} {item.symbol}
      </>
    ),
    []
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <KpiSkeleton />;
  }

  const defaultData: Wallet = {
    wallet_address: '0x',
    total_supply: '0',
    total_supply_change: '0',
    total_supply_change_percentage: '0',
    total_borrow: '0',
    total_borrow_change: '0',
    total_borrow_change_percentage: '0',
    net: '0',
    liquidation_buffer: '0',
    liquidation_buffer_change: '0',
    liquidation_buffer_change_percentage: '0',
    account_liquidity: '0',
    account_liquidity_change: '0',
    account_liquidity_change_percentage: '0',
    health_rate: '0',
    health_rate_change: '0',
    health_rate_change_percentage: '0',
    emode: 0
  };

  const walletData = Object.keys(data).length === 0 ? defaultData : data;

  return (
    <Page
      title={
        <WalletAddress
          gap="s"
          options={{
            showCopyBtn: true,
            showDebankLink: true,
            showZapperLink: true,
            inline: false,
            sizes: {
              icon: '8',
              walletAddressHeader: '4'
            },
            network: network
          }}
          address={walletAddress}
        />
      }>
      <Flex flexDirection="column" gap="m">
        <Flex flexDirection={['column', 'row']} gap={['m', 's']}>
          <KpiCard
            title="Health rate"
            data={walletData}
            field="health_rate"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  nullable: '-',
                  infinityThreshold: 1000,
                  infinityIcon: (
                    <InfinityIcon
                      width={theme.sizes['6']}
                      height={theme.sizes['4']}
                    />
                  )
                }
              }
            }}
          />
        </Flex>
      </Flex>
      <Flex flexDirection={['column', 'row']} gap={['m', 's']}>
        <SupplyPositionsCardList dataTotal={walletData} />
        <BorrowPositionsCardList dataTotal={walletData} />
      </Flex>
      <HistoricTabs walletAddress={walletAddress} />

      <RecentActivity<ActivityEvent>
        heatmapAPIUrl={`/wallets/${walletAddress}/heatmap/`}
        activityAPIUrl={`/wallets/${walletAddress}/events/`}
        activityContentFn={contentFn}
        linkTo="activity"
      />
    </Page>
  );
}
